export type FieldType = 'text' | 'textarea' | 'number' | 'date' | 'datetime' | 'url' | 'email' | 'phone' | 'currency' | 'dropdown' | 'range' | 'user_select' | 'richtext';

export interface FieldOption {
  value: string | number;
  label: string;
}

export interface TaskTypeField {
  name: string;
  label: string;
  type: FieldType;
  required: boolean;
  placeholder?: string;
  options?: FieldOption[]; // For dropdown fields
  min?: number; // For number fields
  max?: number; // For number fields
  rows?: number; // For textarea fields
}

export interface TaskTypeConfig {
  fields: TaskTypeField[];
}

// Field configurations for each task type
export const TASK_TYPE_FIELDS: Record<string, Record<number, TaskTypeConfig>> = {
  gtm: {
    // Whitepaper (card_type: 1)
    1: {
      fields: [
        {
          name: 'title',
          label: 'Title',
          type: 'text',
          required: true,
          placeholder: 'Whitepaper title',
        },
        {
          name: 'topic',
          label: 'Topic',
          type: 'text',
          required: true,
          placeholder: 'Primary subject matter',
        },
        {
          name: 'google_docs_link',
          label: 'Google Docs Link',
          type: 'url',
          required: false,
          placeholder: 'Link to Google Docs document',
        },
        {
          name: 'target_audience',
          label: 'Target Audience',
          type: 'text',
          required: true,
          placeholder: 'Intended reader persona',
        },
        {
          name: 'abstract',
          label: 'Abstract',
          type: 'textarea',
          required: false,
          placeholder: 'Executive summary',
          rows: 4,
        },
        {
          name: 'page_count',
          label: 'Page Count',
          type: 'number',
          required: false,
          placeholder: 'Target or actual pages',
          min: 0,
        },
        {
          name: 'draft_status',
          label: 'Draft Status',
          type: 'dropdown',
          required: false,
          options: [
            { value: 'outline', label: 'Outline' },
            { value: 'first_draft', label: 'First Draft' },
            { value: 'revision', label: 'Revision' },
            { value: 'final', label: 'Final' },
          ],
        },
        {
          name: 'publish_date',
          label: 'Publish Date',
          type: 'date',
          required: false,
        },
        {
          name: 'download_link',
          label: 'Download Link',
          type: 'url',
          required: false,
          placeholder: 'Link to published PDF',
        },
      ],
    },
    // Blog (card_type: 2)
    2: {
      fields: [
        {
          name: 'title',
          label: 'Title',
          type: 'text',
          required: true,
          placeholder: 'Blog post title',
        },
        {
          name: 'excerpt',
          label: 'Excerpt',
          type: 'textarea',
          required: false,
          placeholder: 'Meta description / preview text',
          rows: 3,
        },
        {
          name: 'keywords',
          label: 'Keywords',
          type: 'text',
          required: false,
          placeholder: 'SEO keywords (comma-separated)',
        },
        {
          name: 'author',
          label: 'Author',
          type: 'user_select',
          required: true,
        },
        {
          name: 'platform',
          label: 'Platform',
          type: 'dropdown',
          required: false,
          options: [
            { value: 'company_blog', label: 'Company Blog' },
            { value: 'medium', label: 'Medium' },
            { value: 'linkedin', label: 'LinkedIn' },
            { value: 'other', label: 'Other' },
          ],
        },
        {
          name: 'word_count',
          label: 'Word Count',
          type: 'number',
          required: false,
          placeholder: 'Target or actual word count',
          min: 0,
        },
        {
          name: 'seo_score',
          label: 'SEO Score',
          type: 'range',
          required: false,
          min: 0,
          max: 100,
        },
        {
          name: 'scheduled_date',
          label: 'Scheduled Date',
          type: 'datetime',
          required: false,
        },
        {
          name: 'published_url',
          label: 'Published URL',
          type: 'url',
          required: false,
          placeholder: 'Live post URL',
        },
        {
          name: 'google_docs_link',
          label: 'Google Docs Link',
          type: 'url',
          required: false,
          placeholder: 'Link to Google Docs document',
        },
      ],
    },
    // Podcast (card_type: 3)
    3: {
      fields: [
        {
          name: 'title',
          label: 'Episode Title',
          type: 'text',
          required: true,
          placeholder: 'Episode name',
        },
        {
          name: 'episode_number',
          label: 'Episode Number',
          type: 'number',
          required: false,
          placeholder: 'Episode sequence number',
          min: 1,
        },
        {
          name: 'guest_name',
          label: 'Guest Name',
          type: 'text',
          required: false,
          placeholder: 'Featured guest',
        },
        {
          name: 'guest_title',
          label: 'Guest Title',
          type: 'text',
          required: false,
          placeholder: 'Guest role/company',
        },
        {
          name: 'duration',
          label: 'Duration',
          type: 'number',
          required: false,
          placeholder: 'Length in minutes',
          min: 0,
        },
        {
          name: 'recording_date',
          label: 'Recording Date',
          type: 'date',
          required: false,
        },
        {
          name: 'edit_status',
          label: 'Edit Status',
          type: 'dropdown',
          required: false,
          options: [
            { value: 'scheduled', label: 'Scheduled' },
            { value: 'recorded', label: 'Recorded' },
            { value: 'editing', label: 'Editing' },
            { value: 'final', label: 'Final' },
          ],
        },
        {
          name: 'publish_date',
          label: 'Publish Date',
          type: 'date',
          required: false,
        },
        {
          name: 'show_notes',
          label: 'Show Notes',
          type: 'textarea',
          required: false,
          placeholder: 'Episode description and links',
          rows: 4,
        },
        {
          name: 'audio_file_url',
          label: 'Audio File URL',
          type: 'url',
          required: false,
          placeholder: 'Link to audio file',
        },
      ],
    },
    // Landing Page (card_type: 4) - TODO: Add fields
    4: {
      fields: [
        {
          name: 'title',
          label: 'Title',
          type: 'text',
          required: true,
          placeholder: 'Landing page title',
        },
      ],
    },
    // Email Campaign (card_type: 5) - TODO: Add fields
    5: {
      fields: [
        {
          name: 'title',
          label: 'Title',
          type: 'text',
          required: true,
          placeholder: 'Email campaign title',
        },
      ],
    },
  },
  customer: {
    // Client (card_type: 9) - TODO: Add fields
    9: {
      fields: [
        {
          name: 'title',
          label: 'Title',
          type: 'text',
          required: true,
          placeholder: 'Client name',
        },
      ],
    },
    // Opportunity/Lead (card_type: 10)
    10: {
      fields: [
        {
          name: 'title',
          label: 'Contact Name',
          type: 'text',
          required: true,
          placeholder: 'Primary contact person',
        },
        {
          name: 'company',
          label: 'Company',
          type: 'text',
          required: true,
          placeholder: 'Company name',
        },
        {
          name: 'email',
          label: 'Email',
          type: 'email',
          required: false,
          placeholder: 'Contact email',
        },
        {
          name: 'phone',
          label: 'Phone',
          type: 'phone',
          required: false,
          placeholder: 'Contact phone',
        },
        {
          name: 'industry',
          label: 'Industry',
          type: 'dropdown',
          required: false,
          options: [
            { value: 'technology', label: 'Technology' },
            { value: 'healthcare', label: 'Healthcare' },
            { value: 'finance', label: 'Finance' },
            { value: 'retail', label: 'Retail' },
            { value: 'manufacturing', label: 'Manufacturing' },
            { value: 'education', label: 'Education' },
            { value: 'other', label: 'Other' },
          ],
        },
        {
          name: 'company_size',
          label: 'Company Size',
          type: 'dropdown',
          required: false,
          options: [
            { value: '1-10', label: '1-10' },
            { value: '11-50', label: '11-50' },
            { value: '51-200', label: '51-200' },
            { value: '201-500', label: '201-500' },
            { value: '500+', label: '500+' },
          ],
        },
        {
          name: 'source',
          label: 'Source',
          type: 'dropdown',
          required: false,
          options: [
            { value: 'website', label: 'Website' },
            { value: 'referral', label: 'Referral' },
            { value: 'event', label: 'Event' },
            { value: 'outbound', label: 'Outbound' },
            { value: 'other', label: 'Other' },
          ],
        },
        {
          name: 'lead_score',
          label: 'Lead Score',
          type: 'range',
          required: false,
          min: 0,
          max: 100,
        },
        {
          name: 'arr_potential',
          label: 'ARR Potential',
          type: 'currency',
          required: false,
          placeholder: 'Estimated annual value',
        },
        {
          name: 'next_action',
          label: 'Next Action',
          type: 'text',
          required: false,
          placeholder: 'Next follow-up task',
        },
        {
          name: 'next_action_date',
          label: 'Next Action Date',
          type: 'date',
          required: false,
        },
        {
          name: 'notes',
          label: 'Notes',
          type: 'textarea',
          required: false,
          placeholder: 'Conversation notes',
          rows: 4,
        },
      ],
    },
  },
  finances: {
    // Grant (card_type: 6) - TODO: Add fields
    6: {
      fields: [
        {
          name: 'title',
          label: 'Title',
          type: 'text',
          required: true,
          placeholder: 'Grant name',
        },
      ],
    },
    // Accelerator (card_type: 7) - TODO: Add fields
    7: {
      fields: [
        {
          name: 'title',
          label: 'Title',
          type: 'text',
          required: true,
          placeholder: 'Accelerator name',
        },
      ],
    },
    // VC Contact (card_type: 8) - TODO: Add fields
    8: {
      fields: [
        {
          name: 'title',
          label: 'Title',
          type: 'text',
          required: true,
          placeholder: 'VC Contact name',
        },
      ],
    },
  },
  product: {
    // Product Feature (card_type: 11)
    11: {
      fields: [
        {
          name: 'title',
          label: 'Feature Name',
          type: 'text',
          required: true,
          placeholder: 'Feature name',
        },
        {
          name: 'quarter',
          label: 'Target Quarter',
          type: 'dropdown',
          required: true,
          options: [
            { value: 'Q1 2026', label: 'Q1 2026' },
            { value: 'Q2 2026', label: 'Q2 2026' },
            { value: 'Q3 2026', label: 'Q3 2026' },
            { value: 'Q4 2026', label: 'Q4 2026' },
            { value: 'Q1 2027', label: 'Q1 2027' },
          ],
        },
        {
          name: 'description_detail',
          label: 'Details',
          type: 'textarea',
          required: false,
          placeholder: 'Feature details and requirements',
          rows: 4,
        },
        {
          name: 'priority_label',
          label: 'Priority',
          type: 'dropdown',
          required: false,
          options: [
            { value: 'critical', label: 'Critical' },
            { value: 'high', label: 'High' },
            { value: 'medium', label: 'Medium' },
            { value: 'low', label: 'Low' },
          ],
        },
      ],
    },
    // Data Import (card_type: 12)
    12: {
      fields: [
        {
          name: 'title',
          label: 'Import Name',
          type: 'text',
          required: true,
          placeholder: 'Data import name',
        },
        {
          name: 'quarter',
          label: 'Target Quarter',
          type: 'dropdown',
          required: true,
          options: [
            { value: 'Q1 2026', label: 'Q1 2026' },
            { value: 'Q2 2026', label: 'Q2 2026' },
            { value: 'Q3 2026', label: 'Q3 2026' },
            { value: 'Q4 2026', label: 'Q4 2026' },
            { value: 'Q1 2027', label: 'Q1 2027' },
          ],
        },
        {
          name: 'data_source',
          label: 'Data Source',
          type: 'text',
          required: false,
          placeholder: 'Source system or file format',
        },
        {
          name: 'record_count',
          label: 'Estimated Records',
          type: 'number',
          required: false,
          placeholder: 'Number of records',
          min: 0,
        },
      ],
    },
    // Manufacturer (card_type: 13)
    13: {
      fields: [
        {
          name: 'title',
          label: 'Manufacturer Name',
          type: 'text',
          required: true,
          placeholder: 'Manufacturer name',
        },
        {
          name: 'quarter',
          label: 'Target Quarter',
          type: 'dropdown',
          required: true,
          options: [
            { value: 'Q1 2026', label: 'Q1 2026' },
            { value: 'Q2 2026', label: 'Q2 2026' },
            { value: 'Q3 2026', label: 'Q3 2026' },
            { value: 'Q4 2026', label: 'Q4 2026' },
            { value: 'Q1 2027', label: 'Q1 2027' },
          ],
        },
        {
          name: 'contact',
          label: 'Primary Contact',
          type: 'text',
          required: false,
          placeholder: 'Contact person',
        },
        {
          name: 'website',
          label: 'Website',
          type: 'url',
          required: false,
          placeholder: 'Manufacturer website',
        },
      ],
    },
    // Distributor (card_type: 14)
    14: {
      fields: [
        {
          name: 'title',
          label: 'Distributor Name',
          type: 'text',
          required: true,
          placeholder: 'Distributor name',
        },
        {
          name: 'quarter',
          label: 'Target Quarter',
          type: 'dropdown',
          required: true,
          options: [
            { value: 'Q1 2026', label: 'Q1 2026' },
            { value: 'Q2 2026', label: 'Q2 2026' },
            { value: 'Q3 2026', label: 'Q3 2026' },
            { value: 'Q4 2026', label: 'Q4 2026' },
            { value: 'Q1 2027', label: 'Q1 2027' },
          ],
        },
        {
          name: 'region',
          label: 'Coverage Region',
          type: 'text',
          required: false,
          placeholder: 'Geographic region',
        },
        {
          name: 'contact',
          label: 'Primary Contact',
          type: 'text',
          required: false,
          placeholder: 'Contact person',
        },
      ],
    },
    // Product Line (card_type: 15)
    15: {
      fields: [
        {
          name: 'title',
          label: 'Product Line Name',
          type: 'text',
          required: true,
          placeholder: 'Product line name',
        },
        {
          name: 'quarter',
          label: 'Target Quarter',
          type: 'dropdown',
          required: true,
          options: [
            { value: 'Q1 2026', label: 'Q1 2026' },
            { value: 'Q2 2026', label: 'Q2 2026' },
            { value: 'Q3 2026', label: 'Q3 2026' },
            { value: 'Q4 2026', label: 'Q4 2026' },
            { value: 'Q1 2027', label: 'Q1 2027' },
          ],
        },
        {
          name: 'category',
          label: 'Category',
          type: 'text',
          required: false,
          placeholder: 'Product category',
        },
        {
          name: 'sku_count',
          label: 'SKU Count',
          type: 'number',
          required: false,
          placeholder: 'Number of SKUs',
          min: 0,
        },
      ],
    },
  },
};

// Get field configuration for a specific task type
export function getTaskTypeFields(
  dashboardType: 'gtm' | 'customer' | 'finances' | 'product' | null,
  cardType: number | null
): TaskTypeConfig | null {
  if (!dashboardType || cardType === null) {
    return null;
  }
  return TASK_TYPE_FIELDS[dashboardType]?.[cardType] || null;
}

